import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import { useTranslation } from 'next-i18next';
import { selectChangeStatusDelay, selectCouponPossibleWinAmountV2, selectCouponPreviewV2, selectCouponSelectedBetsV2, selectIsCouponQuickBetsV2, selectShowProgress, selectUserIsAuthenticated, useLocalizedNumberFormatters } from '@mwl/core-lib';
import { couponToastContainerId } from '@/constants';
import { useTypedSelector } from '@/hooks';
import { selectIsShowMobileCoupon } from '@/store';
import { couponToastOptions } from '../../SportCoupon.data';
import { useCouponNotifications } from '../../SportCoupon.hooks';
import { CouponAccountSelect } from '../CouponAccountSelect/CouponAccountSelect';
import { CouponBetsList } from '../CouponBetsList/CouponBetsList';
import { CouponCreateProgress } from '../CouponCreateProgress/CouponCreateProgress';
import { CouponDataRow } from '../CouponDataRow/CouponDataRow';
import type { CouponDataRowClasses } from '../CouponDataRow/CouponDataRow.types';
import { CouponEmptyBlock } from '../CouponEmptyBlock/CouponEmptyBlock';
import { CouponOddsChangeSwitch } from '../CouponOddsChangeSwitch/CouponOddsChangeSwitch';
import { CouponQuickBetSwitch } from '../CouponQuickBetSwitch/CouponQuickBetSwitch';
import { CouponSubmit } from '../CouponSubmit/CouponSubmit';
import { CouponSum } from '../CouponSum/CouponSum';
import { CouponTypeSelect } from '../CouponTypeSelect/CouponTypeSelect';
import styles from './CouponBody.module.scss';
const possibleWinsClasses: CouponDataRowClasses = {
  title: styles.possibleWinsTitle,
  value: styles.possibleWinsValue
};
const CouponBody: FC = () => {
  const {
    t
  } = useTranslation('sport');
  const accountSelectWrapperRef = useRef<HTMLDivElement>(null);
  const scrollContentWrapperRef = useRef<HTMLDivElement>(null);
  const {
    bets
  } = useTypedSelector(selectCouponPreviewV2);
  const isShowMobileCoupon = useTypedSelector(selectIsShowMobileCoupon);
  const possibleAmount = useTypedSelector(selectCouponPossibleWinAmountV2);
  const isQuickBets = useTypedSelector(selectIsCouponQuickBetsV2);
  const isAuth = useTypedSelector(selectUserIsAuthenticated);
  const selectedBets = useTypedSelector(selectCouponSelectedBetsV2);
  const statusDelay = useTypedSelector(selectChangeStatusDelay);
  const isShowProgress = useTypedSelector(selectShowProgress);
  const [updateScrollKey, setUpdateScrollKey] = useState(0);
  const {
    formatPriceByCurrencyCodeWithLocale
  } = useLocalizedNumberFormatters({
    minimumFractionDigits: 2
  });
  useCouponNotifications(couponToastContainerId);
  const handleUpdateScroll = () => {
    setUpdateScrollKey(prev => prev + 1);
  };
  useEffect(() => {
    const scrollContainer = scrollContentWrapperRef.current;
    if (scrollContainer) {
      const {
        scrollTop
      } = scrollContainer;
      const clientRect = accountSelectWrapperRef?.current?.getBoundingClientRect();
      if (scrollTop !== undefined && clientRect?.top !== undefined) {
        scrollContainer.scroll({
          top: 0,
          behavior: 'smooth'
        });
      }
    }
  }, [updateScrollKey]);
  return <div className={styles.root} data-sentry-component="CouponBody" data-sentry-source-file="CouponBody.tsx">
      <div className={cn(styles.header, {
      [styles.withTabs]: !isQuickBets && isAuth
    })}>
        {!isShowMobileCoupon && <CouponQuickBetSwitch disabled={!isAuth} />}
        {!isQuickBets && isAuth && <CouponTypeSelect />}
      </div>
      <div className={styles.main}>
        <div className={styles.contentWrapper} ref={scrollContentWrapperRef}>
          {!isAuth && <div>
              {!isShowMobileCoupon && !isQuickBets && <>{selectedBets.length ? <CouponBetsList bets={bets} /> : <CouponEmptyBlock />}</>}
            </div>}
          {isAuth && <div className={styles.mainContent}>
              {isShowMobileCoupon && <CouponQuickBetSwitch />}
              {!isQuickBets && !!bets?.length && <CouponBetsList bets={bets} />}
              {(!isQuickBets || isQuickBets && !isShowProgress) && <CouponSum />}
              {!isQuickBets && <div ref={accountSelectWrapperRef}>
                  <CouponAccountSelect onSelectItem={handleUpdateScroll} />
                </div>}
              {!isQuickBets && <CouponOddsChangeSwitch />}
            </div>}
        </div>
      </div>
      <div className={styles.footer}>
        {isAuth && !isQuickBets && !!possibleAmount?.amount && <CouponDataRow classes={possibleWinsClasses} className={styles.possibleWins} title={t('coupon.possible_win', 'Possible win:')} value={formatPriceByCurrencyCodeWithLocale(possibleAmount?.amount, possibleAmount?.currency)} />}
        {!isQuickBets && !isShowProgress && <CouponSubmit />}
        {isShowProgress && <CouponCreateProgress duration={statusDelay || 10000} size={isQuickBets ? 'large' : 'small'} />}
      </div>
      <ToastContainer {...couponToastOptions} position="bottom-center" containerId={couponToastContainerId} data-sentry-element="ToastContainer" data-sentry-source-file="CouponBody.tsx" />
    </div>;
};
export { CouponBody };